import { useEffect, useMemo, useState } from 'react'

import { useModalContext } from '@fourthwall/components'
import { useOffersByIdsQuery } from '@hooks/useOffersByIdsQuery'
import { SelectProductsModal } from '@modules/Dashboard/modules/SelectProductsModal'

import type { SelectedProducts } from '../AppsTwitch.types'
import { mapOffersDataToSelectedProducts } from '../utils/mapOffersDataToSelectedProducts'

type Props = {
  onProductsIdsChange?: (ids: string[]) => void
  modalTitle?: string
}

export const useGiftableProducts = ({
  onProductsIdsChange,
  modalTitle = 'Select products',
}: Props = {}): SelectedProducts => {
  const { open } = useModalContext()
  const [productsIds, setProductsIds] = useState<string[]>([])
  const { offersByIdsQuery } = useOffersByIdsQuery([productsIds], {
    enabled: !!productsIds.length,
  })
  const products = useMemo(
    () => mapOffersDataToSelectedProducts(offersByIdsQuery.data),
    [offersByIdsQuery.data],
  )

  useEffect(() => {
    onProductsIdsChange && onProductsIdsChange(productsIds)
  }, [productsIds])

  const removeProductId = (id: string) => {
    const ids = productsIds.filter((productId) => productId !== id)
    setProductsIds(ids)
  }

  const openSelectProductsModal = () =>
    open(SelectProductsModal, {
      title: modalTitle,
      selectedIds: productsIds,
      submitButtonLabel: 'Confirm',
      onSave: (offersIds) => setProductsIds(offersIds),
    })

  return {
    products,
    productsIds,
    isLoading: offersByIdsQuery.isLoading,
    data: Object.values(offersByIdsQuery.data || []),
    setProductsIds,
    onAddProduct: openSelectProductsModal,
    onRemoveProduct: removeProductId,
  }
}
